import { useCallback, useRef, useEffect } from 'react';

interface ScrollLockOptions {
  /** Lock the body scroll when true */
  isLocked: boolean;
  /** Target element to lock (defaults to document.body) */
  target?: HTMLElement;
  /** Whether to preserve scrollbar width to prevent layout shift */
  preserveScrollbarWidth?: boolean;
}

interface ScrollLockReturn {
  /** Current lock state */
  isLocked: boolean;
  /** Manually lock scroll */
  lock: () => void;
  /** Manually unlock scroll */
  unlock: () => void;
}

/**
 * Hook to lock body scroll while preserving scroll position
 * Prevents background shift by compensating for scrollbar width
 * 
 * @example
 * const { lock, unlock } = useScrollLock({ isLocked: modalIsOpen });
 * 
 * @example
 * useScrollLock({ isLocked: sidebarOpen, target: sidebarRef.current });
 */
export function useScrollLock(options: ScrollLockOptions): ScrollLockReturn {
  const { isLocked, target, preserveScrollbarWidth = true } = options;
  
  // Store scroll position and original styles
  const scrollYRef = useRef(0);
  const originalStylesRef = useRef<{
    overflow: string;
    paddingRight: string;
    position: string;
    top: string;
    width: string;
  } | null>(null);

  const getScrollbarWidth = useCallback((): number => {
    return window.innerWidth - document.documentElement.clientWidth;
  }, []);

  const lock = useCallback(() => {
    const element = target || document.body;
    
    // Save current scroll position
    scrollYRef.current = window.scrollY || document.documentElement.scrollTop;
    
    // Save original styles
    const computedStyle = window.getComputedStyle(element);
    originalStylesRef.current = {
      overflow: element.style.overflow || computedStyle.overflow,
      paddingRight: element.style.paddingRight || computedStyle.paddingRight,
      position: element.style.position || computedStyle.position,
      top: element.style.top || computedStyle.top,
      width: element.style.width || computedStyle.width,
    };

    // Calculate scrollbar width
    const scrollbarWidth = preserveScrollbarWidth ? getScrollbarWidth() : 0;

    // Apply lock styles
    element.style.overflow = 'hidden';
    element.style.position = 'relative';
    element.style.top = '0';
    
    // Compensate for scrollbar width to prevent layout shift
    if (scrollbarWidth > 0) {
      element.style.paddingRight = `${scrollbarWidth}px`;
    }

    // Preserve scroll position on html element for iOS Safari
    document.documentElement.style.scrollBehavior = 'auto';
    
  }, [target, preserveScrollbarWidth, getScrollbarWidth]);

  const unlock = useCallback(() => {
    const element = target || document.body;
    const savedStyles = originalStylesRef.current;
    
    if (!savedStyles) return;

    // Restore original styles
    element.style.overflow = savedStyles.overflow;
    element.style.paddingRight = savedStyles.paddingRight;
    element.style.position = savedStyles.position;
    element.style.top = savedStyles.top;
    element.style.width = savedStyles.width;

    // Restore scroll position
    window.scrollTo({
      top: scrollYRef.current,
      behavior: 'auto'
    });

    // Reset html scroll behavior
    document.documentElement.style.scrollBehavior = '';
    
    // Clear saved styles
    originalStylesRef.current = null;
  }, [target]);

  // Handle lock state changes
  useEffect(() => {
    if (isLocked) {
      lock();
    } else {
      unlock();
    }

    // Cleanup on unmount
    return () => {
      if (originalStylesRef.current) {
        unlock();
      }
    };
  }, [isLocked, lock, unlock]);

  return {
    isLocked,
    lock,
    unlock,
  };
}

export default useScrollLock;
